import React, { useEffect } from 'react';
import { Box, Divider, Grid, List, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { Button, Paper, TestDataAttribute } from '@roc/ui';
import { observer } from 'mobx-react';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  APPRAISAL_PAYMENT_LOAN,
  APPRAISAL_PAYMENT_STANDALONE
} from '../constants/tamariskPayments';
import { useTamariskPaymentsStore } from '../hooks/useTamariskPaymentsStore';
import CustomListItem from './customListItem';
import { PortalBannerMessages } from '@roc/feature-app-core';
import { useStore } from '@roc/client-portal-shared/hooks';
import { WarningOutlined } from '@material-ui/icons';
import { DisclaimerMessage } from './disclaimerMessage';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      margin: '0 auto',
      borderRadius: theme.spacing(2),
      padding: theme.spacing(3, 0),
      boxShadow:
        'rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px',
    },
    secondAppraisalPaper: {
      margin: '15px auto',
      borderRadius: theme.spacing(2),
      padding: theme.spacing(3, 0),
      boxShadow:
        'rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px',
    },
    textBold: {
      fontWeight: 700,
    },
    textItalic: {
      fontStyle: 'italic',
    },
    totalCost: {
      marginTop: '20Px',
    },
  })
);

const SECONDARY_VALUATION_FEE = 'Secondary Valuation Fee';

interface Props {
  handleNextStep: () => void;
  entityId: string;
  tamariskFlowType: string;
}

const TamariskPaymentInformation = observer((props: Props) => {
  const { tamariskPaymentsStore } = useTamariskPaymentsStore();
  const tamariskDocumentStore = tamariskPaymentsStore.tamariskDocumentStore;
  const { loading, form } = tamariskDocumentStore;
  const { paper, textBold, totalCost, secondAppraisalPaper } = useStyles();

  const divider = <Divider variant="middle" component="li" />;

  const renderPaymentDetails = () => (
    <Box mt={3}>
      <Box mt={3}>
        <DisclaimerMessage />
        <Paper className={paper}>
          <Box mb={1} px={2}>
            <Typography className={textBold} variant="subtitle1">
              Appraisal Cost estimation is based on:
            </Typography>
          </Box>
          <List>
            {tamariskDocumentStore.properties?.map((prop) => (
              <React.Fragment key={prop.description}>
                {(prop.address && prop.description === 'Appraisal Fee') && (
                  <CustomListItem
                    key={prop.address}
                    label={prop.address}
                    value=""
                  />
                )}
                <div style={{ marginLeft: '20px' }}>
                  <CustomListItem
                    key={prop.description}
                    label={`${prop.description} ${prop.alreadyPaid ? '(already paid)' : ''
                      }`}
                    value={`$${prop.appraisalCost}`}
                  />
                </div>
              </React.Fragment>
            ))}
            {divider}
            <CustomListItem
              key="Total"
              label="Total Appraisal Cost"
              value={`$${tamariskDocumentStore.totalAppraisalCost}`}
            />
          </List>
        </Paper>
      </Box>
    </Box>
  );


  const renderPaymentDetailsRental = () => (
    <Box mt={3}>
      <Box mt={3}>
        <DisclaimerMessage />
        <Paper className={secondAppraisalPaper}>
          <Box mb={1} px={2}>
            <Typography className={textBold} variant="subtitle1">
              Appraisal Cost estimation is based on:
            </Typography>
          </Box>
          <List>
            {tamariskDocumentStore.properties?.map((prop) => (
              <React.Fragment key={prop.description}>
                {(prop.address && prop.description === 'Appraisal Fee') && (
                  <CustomListItem
                    key={prop.address}
                    label={prop.address}
                    value=""
                  />
                )}
                {prop.description != SECONDARY_VALUATION_FEE && <div style={{ marginLeft: '20px' }}>
                  <CustomListItem
                    key={prop.description}
                    label={`${prop.description} ${prop.alreadyPaid ? '(already paid)' : ''
                      }`}
                    value={`$${prop.appraisalCost}`}
                  />
                </div>}
              </React.Fragment>
            ))}
            {divider}
          </List>
        </Paper>
        <Paper className={secondAppraisalPaper}>
          <Box mb={1} px={2}>
            <Typography className={textBold} variant="subtitle1">
              Appraisal Cost estimation is based on:
            </Typography>
          </Box>
          <List>
            {tamariskDocumentStore.properties?.map((prop) => (
              <React.Fragment key={prop.description}>
                {(prop.description === SECONDARY_VALUATION_FEE) && (
                  <CustomListItem
                    key={prop.address}
                    label={prop.address}
                    value=""
                  />
                )}
                {prop.description == SECONDARY_VALUATION_FEE && <div style={{ marginLeft: '20px' }}>
                  <CustomListItem
                    key={prop.description}
                    label={`${prop.description} ${prop.alreadyPaid ? '(already paid)' : ''
                      }`}
                    value={`$${prop.appraisalCost}`}
                  />
                </div>}
              </React.Fragment>
            ))}
            {divider}
          </List>
        </Paper>
        <Paper className={secondAppraisalPaper}>
          <CustomListItem
            key="Total"
            label="Total Appraisal Cost"
            value={`$${tamariskDocumentStore.totalAppraisalCost}`}
          />
        </Paper>
      </Box>
    </Box>
  );

  const renderErrorMessage = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error">
          <AlertTitle>Warning</AlertTitle>
          {`${tamariskDocumentStore.feeCalcError}`}
        </Alert>
      </Grid>
    )
  }

  const renderLoading = () => {
    return (
      <Grid item xs={12}>
        <Typography variant="subtitle1">
          Loading...
        </Typography>
      </Grid>
    )
  }

  useEffect(() => {
    switch (props.tamariskFlowType) {
      case APPRAISAL_PAYMENT_LOAN:
        tamariskDocumentStore.fetchLoanInfo(
          props.entityId,
          form.fields.fullName.value
        );
        break;
      case APPRAISAL_PAYMENT_STANDALONE:
        tamariskDocumentStore.fetchStandaloneInfo(
          props.entityId,
          form.fields.fullName.value
        );
        break;
      default:
        break;
    }
  }, []);

  return (
    <>
      <Box style={{ maxWidth: 840, margin: 'auto' }}>
        <Box>
          <Grid container justifyContent="flex-start" spacing={2}>
            <Box mb={2}>
              <Typography variant="h4">Appraisal Cost Details</Typography>
            </Box>
          </Grid>
        </Box>
        {
          loading ? renderLoading() : (tamariskDocumentStore.feeCalcError ? renderErrorMessage() : tamariskDocumentStore.isCombinedPayment ? renderPaymentDetailsRental() : renderPaymentDetails())
        }
        <Box mt={16}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={props.handleNextStep}
                testId="next"
                disabled={(tamariskDocumentStore.feeCalcError || loading) ? true : false}
              >
                {tamariskPaymentsStore.paymentStatus ? 'Next' : 'Next'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
});

export default TamariskPaymentInformation;
